import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { Typography, Theme, Button, Alert } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Spinner } from '@globalvisioninc/ui/spinner';
import {
  getInspectionId,
  getTextProcessStatus,
  getUnconfirmTextZoneError,
  getUploadedFilesSelector,
  inspection,
  getStartingInspection,
  app,
  getName,
  getNoResultAnnotationId,
  getZoneSelectedTexts,
  getExcludedPage,
  getUnconfirmGraphicZoneError,
  getUnconfirmedMarqueeZoneError,
  getNumberOfAnnotations,
  getManualAlignTrackingData,
  getSelectedTool,
  getSpellingTrackingData,
  getManualSelectedZoneId,
  getAutoMatchGraphic,
  getSourceLayers,
  getTargetLayers,
  getSourceSeparations,
  getTargetSeparations,
  getMultiFileNamesJoined,
  getIsSingleFile,
  getSelectedCustomDictionaryIDs,
  getInspectionHasOutdatedGraphicZones,
  getHighSensitivitySelected,
  getOneToOneSelected,
  getTextOneToOneSelected,
  getPagesCropZones,
  getPendingCropZone,
  getPendingShapesRequest,
  getChangeToolError,
  getAllAnnotationsLoaded,
  getDocumentsLoaded,
  getUpdateInspectionData,
  getInspectionSettings,
  getFilesMetadata,
} from 'store';
import { startInspection, selectUnconfirmedZones } from 'store/requests';
import { GVActionButton, GVInspectionPanel } from 'components/common';
import { GVIcon } from 'components/icons';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { DocumentTypes, PDFTronTools, PDFTRON_DEFAULT_TOOL, MarqueeZoneCustomData, StoreState } from 'types';
import { useConfirmation } from 'utils';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { getStyleVariables } from 'styles/vars';
import { PDFManagerFactory, useAnnotationTools } from 'pdftron';
import GVTextButton from 'components/lib/GVTextButton/GVTextButton';
import { resetRequests } from '@redux-requests/core';
import { fetchDifferences } from 'store/request/differences/actions';
import { useTracker } from '../../Tracker/TrackerProvider';
import AnnotationsTools, { toggleTool } from './AnnotationsTools';
import PointerBoundary from './PointerBoundary';
import { makeStyles } from 'tss-react/mui';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { useCustomDictionaries } from 'store/queries/dictionaries/customDictionaries';
import { startShapesJob } from 'store/request/files/actions';
import { useMarqueeZoneStateStore } from 'zstore/marqueeZoneStore';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';
import { isResultUrl } from 'utils/location';
import { isPrepLocked, shouldRedirectSharedInspectionToResults } from './sharedRedirect';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { updateInspection } from 'store/request/inspections/actions';
import selectTool from 'utils/selectTool/selectTool';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import toastEvents, { ToastTypes } from 'components/DocumentToast/toastEvents';
import styles from './PrepTab.module.css';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    // MuiAlert is not yet part of the DeprecatedThemeOptions.Override type so we can't add it in the customizedTheme
    alert: {
      borderRadius: 0,
    },
    alertMessage: {
      padding: theme.spacing(1, 0),
      fontSize: '11px',
    },
    warningIcon: {
      color: theme.palette.warning.main,
      alignItems: 'center',
    },
    scrollBar: styleVariables.scrollBar,
    noColorPointsDialogPaper: {
      width: 388,
      maxWidth: 388,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 24,
      padding: 16,
      // Paper uses alignItems:flex-start so DialogActions would shrink to content; stretch so actions row is full width and buttons can sit on the right
      '& .MuiDialogActions-root': {
        alignSelf: 'stretch',
        justifyContent: 'flex-end',
        padding: 0,
        marginTop: 0,
      },
    },
    noColorPointsTitle: {
      padding: 0,
      width: '100%',
      boxSizing: 'border-box',
      '& .MuiTypography-root': {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: '17px',
        color: '#FFF',
      },
    },
    noColorPointsContent: {
      padding: 0,
      marginBottom: 0,
      '& .MuiTypography-root': {
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '17px',
        color: '#FFF',
      },
    },
    noColorPointsActionsRow: {
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      width: '100%',
    },
    noColorPointsCancelButton: {
      display: 'flex',
      padding: '4px 12px 8px 12px',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 4,
      border: '1px solid var(--gds-color-cool-gray-900, #101113)',
      background: 'var(--gds-color-cool-gray-500, #292B30)',
      color: '#FFF',
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '17px',
      '&:hover': {
        background: 'var(--gds-color-cool-gray-500, #292B30)',
        border: '1px solid var(--gds-color-cool-gray-900, #101113)',
      },
    },
    noColorPointsPrimaryButton: {
      display: 'flex',
      padding: '8px 16px 9px 16px',
      alignItems: 'center',
      borderRadius: 4,
      opacity: 1,
      color: '#FFF',
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '17px',
      boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 1px 5px 0 rgba(0, 0, 0, 0.2)',
      '&.MuiButton-containedSecondary': {
        backgroundColor: '#eb4542',
        '&:hover': {
          backgroundColor: '#f56e6a',
          boxShadow:
            '0 2px 4px 0 rgba(0, 0, 0, 0.18), 0 3px 3px -2px rgba(0, 0, 0, 0.14), 0 1px 8px 0 rgba(0, 0, 0, 0.22)',
        },
      },
    },
  };
});

const PrepTabAlerts = () => {
  const { classes } = useStyles();
  const documentsLoaded = useSelector(getDocumentsLoaded);
  const { masterMetadata, sampleMetadata } = useSelector(getFilesMetadata);
  const { source, target } = useSelector(getUploadedFilesSelector);

  const hasType3FontWarning =
    documentsLoaded && (masterMetadata?.containsType3Font || sampleMetadata?.containsType3Font);

  if (!hasType3FontWarning) {
    return null;
  }

  // Check if either file with Type3 fonts is also a merged file
  const masterIsMergedWithType3 =
    masterMetadata?.containsType3Font && source.mergedFileData && source.mergedFileData.length > 0;
  const sampleIsMergedWithType3 =
    sampleMetadata?.containsType3Font && target.mergedFileData && target.mergedFileData.length > 0;
  const hasMergedType3File = masterIsMergedWithType3 || sampleIsMergedWithType3;

  return (
    <Alert
      className={classes.alert}
      severity="warning"
      icon={<ErrorOutlineIcon fontSize="inherit" />}
      classes={{ message: classes.alertMessage, icon: classes.warningIcon }}
    >
      {hasMergedType3File ? (
        <>
          Your PDF file(s) appear to be exported from Figma, and text was extracted automatically.{' '}
          <strong>Live Text isn't supported and may show no text when the toggle is enabled</strong> – this is expected.
        </>
      ) : (
        <>
          Your PDF file(s) appear to be exported from Figma, and text was extracted automatically.{' '}
          <strong>Live Text isn't supported and may show no text when the toggle is enabled</strong> – this is expected.
          You can safely skip OCR unless you need to zone.
        </>
      )}
    </Alert>
  );
};

const PrepTab = () => {
  const tracker = useTracker();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const textProcessCompleted = useSelector(getTextProcessStatus);
  const annotationLoaded = useSelector(getAllAnnotationsLoaded);
  const documentsLoaded = useSelector(getDocumentsLoaded);
  const inspectionId = useSelector(getInspectionId);
  const inspectionName = useSelector(getName);
  const unconfirmTextZoneError = useSelector(getUnconfirmTextZoneError);
  const unconfirmGraphicZoneError = useSelector(getUnconfirmGraphicZoneError);
  const unconfirmMarqueeZoneError = useSelector(getUnconfirmedMarqueeZoneError);
  const pendingCropZone = useSelector(getPendingCropZone);
  const pendingShapeRequest = useSelector(getPendingShapesRequest);
  const noResultAnnotationId = useSelector(getNoResultAnnotationId);
  const zoneSelectedTexts = useSelector(getZoneSelectedTexts);
  const excludedPage = useSelector(getExcludedPage);
  const confirm = useConfirmation();
  const startingInspection = useSelector(getStartingInspection);
  const numberOfAnnotations = useSelector(getNumberOfAnnotations);
  const spellingTrackingData = useSelector(getSpellingTrackingData);
  const { source, target } = useSelector(getUploadedFilesSelector);
  const selectedTool = useSelector(getSelectedTool);
  const manualSelectedZoneId = useSelector(getManualSelectedZoneId);
  // getUpdateInspectionData, getPagesCropZones and getManualAlignTrackingData are only needed when
  // a handler runs, and getUpdateInspectionData additionally reads imperative PDFTron state so it
  // can never be reference-stable. Subscribing would re-render this whole panel on every dispatch.
  const store = useStore<StoreState>();
  const settings = useSelector(getInspectionSettings);
  const autoMatchGraphic = useSelector(getAutoMatchGraphic);
  const { customDictionaryList } = useCustomDictionaries({ getAll: true }); // this is used only because of tracking. The actual call that lists is in the dictionary select component
  const outdatedGraphicZones = useSelector(getInspectionHasOutdatedGraphicZones);
  const oneToOneSelected = useSelector(getOneToOneSelected);
  const textOneToOneSelected = useSelector(getTextOneToOneSelected);
  const highSensitivity = useSelector(getHighSensitivitySelected);
  const changeToolError = useSelector(getChangeToolError);
  const validFontSizeCheck = !settings.fontSizeCheckValue || settings.fontSizeCheckValue >= 4;
  const navigate = useNavigate();
  const { id: routeInspectionId } = useParams();
  // Use the router location (not the global window.location) so the result-page
  // check stays consistent with routeInspectionId/loaded state within a single
  // render. window.location is mutated synchronously by navigate(), so a
  // re-render that fires before the router context updates would see the new
  // path while the route/loaded ids still point at the previous inspection —
  // wrongly bouncing the user back to the old shared inspection's results.
  const location = useLocation();

  const { isShared } = useCollaborators(inspectionId);
  const prepLocked = isPrepLocked({
    routeInspectionId,
    loadedInspectionId: inspectionId,
    isShared,
    isImported: settings.isImported,
  });

  const customDictionaryNames = useSelector(getSelectedCustomDictionaryIDs).map(
    (id) => customDictionaryList.find((dictionary) => dictionary.id === id)?.name,
  );
  const isSingleFile = useSelector(getIsSingleFile);
  const colorCheckRef = useRef(settings.colorCheck);
  colorCheckRef.current = settings.colorCheck;

  useAnnotationTools();

  const PDFDocManager = PDFManagerFactory.getPDFDocManager();
  const checkUnconfirmedGraphicZone = PDFDocManager?.checkUnconfirmedGraphicZones();
  const hasUnconfirmedZones = PDFDocManager?.hasUnconfirmedZones() || false;
  const textUnconfirmedZones = PDFDocManager?.getTextUnconfirmedZones() || [];
  const hasConfirmedGraphicZones = PDFDocManager?.hasConfirmedGraphicZones();

  const sourceInstance = PDFDocManager?.getInstance(DocumentTypes.source);
  const newInstance = PDFDocManager?.getInstance(DocumentTypes.target);
  const sourceHiddenLayers =
    useSelector(getSourceLayers)
      ?.filter((layer) => !layer.visible)
      .map((layer) => layer.name) || [];
  const newHiddenLayers =
    useSelector(getTargetLayers)
      ?.filter((layer) => !layer.visible)
      .map((layer) => layer.name) || [];
  const sourceHiddenSeparations =
    useSelector(getSourceSeparations)
      ?.filter((separation) => !separation.enabled)
      .map((separation) => separation.name) || [];
  const newHiddenSeparations =
    useSelector(getTargetSeparations)
      ?.filter((separation) => !separation.enabled)
      .map((separation) => separation.name) || [];
  const mergedFileNamesTarget = useSelector(getMultiFileNamesJoined(DocumentTypes.target));
  const mergedFileNamesSource = useSelector(getMultiFileNamesJoined(DocumentTypes.source));
  const sendTrackerNotification = async () => {
    const crops = getPagesCropZones(store.getState());
    const manualAlignTrackingData = getManualAlignTrackingData(store.getState());
    const sourcePageQty = sourceInstance?.getTotalPages();
    const sourceFileSize = await sourceInstance?.docViewer.getDocument().getFileSize();
    const newPageQty = newInstance?.getTotalPages();
    const newFileSize = await newInstance?.docViewer.getDocument().getFileSize();
    tracker.track({
      name: 'Document-inspection',
      data: {
        sourceFileName: source.originalName || mergedFileNamesSource,
        newFileName: target.originalName || mergedFileNamesTarget,
        sourceHiddenLayers,
        newHiddenLayers,
        sourceHiddenSeparations,
        newHiddenSeparations,
        sourcePageQty,
        newPageQty,
        sourceFileSize: !isSingleFile ? sourceFileSize : '',
        newFileSize,
        inspectionName,
        sourceExcludedPage: excludedPage.source,
        newExcludedPage: excludedPage.target,
        sourceOCR: !!source.isOCR,
        newOCR: !!target.isOCR,
        fullPageGraphics: settings?.fullPageGraphics,
        shiftedGraphicsAnnotationsQty: numberOfAnnotations[PDFTronTools.GRAPHIC].source.shifted || 0,
        scaledGraphicsAnnotationQty: numberOfAnnotations[PDFTronTools.GRAPHIC].source.scaled || 0,
        sourceCrossoutAnnotationsQty: numberOfAnnotations[PDFTronTools.CROSSOUT].source || 0,
        newCrossoutAnnotationsQty: numberOfAnnotations[PDFTronTools.CROSSOUT].target || 0,
        textZoneAnnotationsQty: numberOfAnnotations[PDFTronTools.ZONE].source || 0,
        marqueeZoneAnnotationsQty: numberOfAnnotations[PDFTronTools.MARQUEE_ZONE].source || 0,
        sourceCropToolAnnotationsQty: numberOfAnnotations[PDFTronTools.CROP].source || 0,
        targetCropToolAnnotationsQty: numberOfAnnotations[PDFTronTools.CROP].target || 0,
        barcodeInspection: settings?.barcode || false,
        ...(settings?.barcodeSettings?.customProfileId &&
          settings?.barcodeSettings?.customProfileId !== 'Default' &&
          settings?.barcodeSettings?.customProfileName && {
            customBarcodeProfile: {
              name: settings.barcodeSettings.customProfileName,
              id: settings.barcodeSettings.customProfileId,
            },
          }),
        brailleLanguage: settings?.brailleLanguage,
        brailleInspection: !!settings?.brailleLanguage,
        inspectionMode: isSingleFile ? 'proofread' : 'compare',
        spellCheck: spellingTrackingData,
        customDict: customDictionaryNames,
        autoMatchFail: autoMatchGraphic?.autoMatchFail,
        autoMatchPass: autoMatchGraphic?.autoMatchPass,
        autoGraphicsAnnotationsQty: numberOfAnnotations[PDFTronTools.GRAPHIC].source.autoGraphic || 0,
        matchedGraphicsAnnotationsQty: numberOfAnnotations[PDFTronTools.GRAPHIC].source.matchedGraphic || 0,
        graphics: !!settings.graphics,
        sourceGraphicsCrop: crops.source.length,
        newGraphicsCrop: crops.target.length,
        sensitivityGraphics: highSensitivity ? 'High' : 'Standard',
        sortedGraphics: !!oneToOneSelected,
        text: !!settings.text,
        sortedText: !!textOneToOneSelected,
        qrdMode: !!settings.qrdMode,
        fontSizeCheck: !!settings.fontSizeCheckValue,
        ...(!!settings.fontSizeCheckValue && { fontSizeCheckValue: settings.fontSizeCheckValue }),
        checkResultsAgainstAnnotations: !!settings.checkResultsAgainstAnnotations,
        extractEmbeddedImages: !!settings.extractEmbeddedImages,
        colorInspection: !!settings.colorCheck,
        alignMatch: manualAlignTrackingData.alignMatch,
        scaleMatch: manualAlignTrackingData.scaleMatch,
        stretchMatch: manualAlignTrackingData.stretchMatch,
      },
    });
  };

  // Shared flow to start inspection after validations
  const startInspectionFlow = () => {
    if (inspectionId) {
      sendTrackerNotification();
      const updateInspectionData = getUpdateInspectionData(store.getState());
      dispatch(resetRequests([fetchDifferences.toString()]));
      dispatch(
        updateInspection(inspectionId, {
          ...(updateInspectionData || {}),
          settings: {
            ...(updateInspectionData?.settings || {}),
            resyncCount: 1,
            cumulativeNetResyncedDifferences: 0,
          },
        }),
      );

      dispatch(app.actions.clearSnackMessageType('success')); // to get rid of the "Undo resync" toast if it's still present
      useDocumentToastStateStore.getState().expireAllToast();
      PDFManagerFactory.getPDFDocManager()?.cleanDocumentHighlight();

      if (settings.colorCheck) {
        const colorPointCount =
          PDFManagerFactory.getPDFDocManager()
            ?.getInstance(DocumentTypes.target)
            ?.annotationManager?.getAnnotationsList()
            ?.filter((annot) => annot.getCustomData('colorPickerAnnotation') === 'true')?.length ?? 0;

        tracker.track({
          name: 'color-inspection-run',
          data: {
            inspectionMode: isSingleFile ? 'proofread' : 'compare',
            colorPointCount,
          },
        });
      }

      PDFTronManager.setToolMode(PDFTRON_DEFAULT_TOOL);
      dispatch(app.actions.setStartingInspection(true));
      dispatch(startInspection());
      dispatch(inspection.actions.setOpenPanel(false));
      // just so when going back to prep page, auto selection is still working
      dispatch(inspection.actions.selectingZone(false));
      dispatch(inspection.actions.setSelectedTool({ tool: PDFTRON_DEFAULT_TOOL }));
      dispatch(app.actions.clearDialogsFromState());
    }
  };

  const handleGoToUnconfirmedZone = () => {
    // select the first text unconfirmed zone
    const firstTextUnconfirmedZone = textUnconfirmedZones[0];
    if (firstTextUnconfirmedZone) {
      const sourceInstance = PDFManagerFactory.getPDFDocManager()?.getInstance(DocumentTypes.source);
      if (sourceInstance) {
        sourceInstance.annotationManager.deselectAllAnnotations();
        sourceInstance.annotationManager.jumpToAnnotation(firstTextUnconfirmedZone);
        sourceInstance.annotationManager.selectAnnotation(firstTextUnconfirmedZone);
      }
    }
  };

  const handleGoToUnconfirmedMarqueeZone = () => {
    const pendingZone = useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber;
    if (!pendingZone) return;
    const sourceInstance = PDFManagerFactory.getPDFDocManager()?.getInstance(DocumentTypes.source);
    const sourceAnnots = sourceInstance?.annotationManager.getAnnotationsList() || [];
    const firstMarquee = sourceAnnots.find(
      (annot) =>
        annot.ToolName === PDFTronTools.MARQUEE_ZONE &&
        parseInt(annot.getCustomData(MarqueeZoneCustomData.zoneId), 10) === pendingZone,
    );
    if (firstMarquee && sourceInstance) {
      sourceInstance.annotationManager.deselectAllAnnotations();
      sourceInstance.annotationManager.jumpToAnnotation(firstMarquee);
      sourceInstance.annotationManager.selectAnnotation(firstMarquee);
    }
  };

  const checkHasColorPoints = () =>
    !settings.colorCheck ||
    (PDFManagerFactory.getPDFDocManager()
      ?.getInstance(DocumentTypes.target)
      ?.annotationManager?.getAnnotationsList()
      ?.some((annot) => annot.getCustomData('colorPickerAnnotation') === 'true') ??
      false);

  const showNoColorPointsWarning = () => {
    confirm({
      variant: 'call-to-action',
      catchOnCancel: false,
      showCloseButton: true,
      paperClassName: classes.noColorPointsDialogPaper,
      titleClassName: classes.noColorPointsTitle,
      contentClassName: classes.noColorPointsContent,
      callToActionActionsClassName: classes.noColorPointsActionsRow,
      title: 'No Color Points Selected',
      description: (
        <>
          Please select at least one color point on the <em>New</em> document to proceed.
        </>
      ),
      additionalActions: (
        <>
          <Button
            className={classes.noColorPointsCancelButton}
            onClick={() => dispatch(app.actions.setCloseConfirm(true))}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            className={classes.noColorPointsPrimaryButton}
            onClick={() => {
              dispatch(app.actions.setCloseConfirm(true));
              const pdfDocManager = PDFManagerFactory.getPDFDocManager();
              if (pdfDocManager?.canChangeTools(PDFTronTools.COLOR_PICKER)) {
                useDocumentToastStateStore
                  .getState()
                  .setDocumentToast(DocumentTypes.target, { ...toastEvents[ToastTypes.COLOR_CHECK], expired: false });
              }
              selectTool(PDFTronTools.COLOR_PICKER);
            }}
          >
            Select a Color Point
          </Button>
        </>
      ),
    });
  };

  const handleRemoveUnconfirmedAndProceed = async () => {
    const docMgr = PDFManagerFactory.getPDFDocManager();
    docMgr?.deleteZoneByAnnotation(textUnconfirmedZones[0], true);

    if (checkHasColorPoints()) {
      dispatch(app.actions.setAutosaving(true));
      dispatch(app.actions.setCloseConfirm(true));
      dispatch(inspection.actions.startInspectionAfterUpdatingAnnotations());
    } else {
      showNoColorPointsWarning();
    }
  };

  const handleRemoveUnconfirmedMarqueeAndProceed = async () => {
    const pdfDocMgr = PDFManagerFactory.getPDFDocManager();
    if (!pdfDocMgr) return;
    const pendingZone = useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber;
    if (!pendingZone) return;

    // Reuse the same logic as the delete popup so behavior stays consistent
    const sourceInstance = pdfDocMgr.getInstance(DocumentTypes.source);
    if (!sourceInstance) return;
    const sourceAnnots = sourceInstance.annotationManager
      .getAnnotationsList()
      .filter(
        (a) =>
          a.ToolName === PDFTronTools.MARQUEE_ZONE &&
          parseInt(a.getCustomData(MarqueeZoneCustomData.zoneId), 10) === pendingZone,
      );

    pdfDocMgr?.deleteMarqueeZoneByAnnotation?.(sourceAnnots[0], true);

    // Hard reset tool state to avoid lingering marquee draw mode on target
    dispatch(inspection.actions.deactivateManualSelection());
    useMarqueeZoneStateStore.getState().setPendingTargetMarqueeZoneNumber(0);
    PDFTronManager.setToolMode(PDFTRON_DEFAULT_TOOL);
    PDFManagerFactory.getViewer(DocumentTypes.source)?.addDocumentHighlight();
    PDFManagerFactory.getViewer(DocumentTypes.target)?.removeAnnotationDocumentHighlight();

    if (checkHasColorPoints()) {
      dispatch(app.actions.setAutosaving(true));
      dispatch(app.actions.setCloseConfirm(true));
      dispatch(inspection.actions.startInspectionAfterUpdatingAnnotations());
    } else {
      showNoColorPointsWarning();
    }
  };

  const renderUnconfirmedZoneActions = () => (
    <>
      <Button
        className={styles.action}
        variant="outlined"
        color="primary"
        id="remove-proceed-text-zone"
        onClick={() => {
          handleRemoveUnconfirmedAndProceed();
        }}
      >
        Remove & Proceed with Inspection
      </Button>
      <Button
        id="go-to-zone-button"
        color="secondary"
        variant="contained"
        onClick={() => {
          handleGoToUnconfirmedZone();
          dispatch(app.actions.setCloseConfirm(true));
        }}
      >
        Go to Zone
      </Button>
    </>
  );

  const renderUnconfirmedMarqueeZoneActions = () => (
    <>
      <Button
        className={styles.action}
        variant="outlined"
        color="primary"
        id="remove-proceed-marquee-zone"
        onClick={() => {
          handleRemoveUnconfirmedMarqueeAndProceed();
        }}
      >
        Remove & Proceed with Inspection
      </Button>
      <Button
        id="go-to-marquee-zone-button"
        color="secondary"
        variant="contained"
        onClick={() => {
          handleGoToUnconfirmedMarqueeZone();
          dispatch(app.actions.setCloseConfirm(true));
        }}
      >
        Go to Zone
      </Button>
    </>
  );

  const handleStartInspection = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (outdatedGraphicZones) dispatch(inspection.actions.deleteOutdatedGraphicZones()); // Crop tool is enabled but old graphic zones still exist

    // check if there are any missing zone numbers for text and marquee zones
    let hasMissingZones = false;
    const docManager = PDFManagerFactory.getPDFDocManager();
    if (!docManager) return;
    const inputAnnotations = docManager.getInputAnnotations();

    const sourceTextZones = inputAnnotations[DocumentTypes.source].filter(
      (annot) => annot.usedTool === PDFTronTools.ZONE,
    );
    const targetTextZones = inputAnnotations[DocumentTypes.target].filter(
      (annot) => annot.usedTool === PDFTronTools.ZONE,
    );

    const sourceTextZoneNumbers = new Set(sourceTextZones.map((zone) => zone.number));
    const targetTextZoneNumbers = new Set(targetTextZones.map((zone) => zone.number));

    if (
      sourceTextZoneNumbers.size !== targetTextZoneNumbers.size ||
      [...sourceTextZoneNumbers].some((value) => !targetTextZoneNumbers.has(value))
    ) {
      hasMissingZones = true;
    }

    const sourceMarqueeZones = inputAnnotations[DocumentTypes.source].filter(
      (annot) => annot.usedTool === PDFTronTools.MARQUEE_ZONE,
    );
    const targetMarqueeZones = inputAnnotations[DocumentTypes.target].filter(
      (annot) => annot.usedTool === PDFTronTools.MARQUEE_ZONE,
    );

    const sourceMarqueeZoneNumbers = new Set(sourceMarqueeZones.map((zone) => zone.number));
    const targetMarqueeZoneNumbers = new Set(targetMarqueeZones.map((zone) => zone.number));

    if (
      sourceMarqueeZoneNumbers.size !== targetMarqueeZoneNumbers.size ||
      [...sourceMarqueeZoneNumbers].some((value) => !targetMarqueeZoneNumbers.has(value))
    ) {
      hasMissingZones = true;
    }

    const hasNoColorPoints = !checkHasColorPoints();

    if (
      !hasUnconfirmedZones &&
      !checkUnconfirmedGraphicZone &&
      !hasMissingZones &&
      useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber === 0 &&
      !hasNoColorPoints
    ) {
      startInspectionFlow();
    } else {
      if (textUnconfirmedZones.length > 0) {
        confirm({
          variant: 'call-to-action',
          catchOnCancel: false,
          title: 'Unconfirmed Text Zone',
          description: 'An unconfirmed text zone was found. What would you like to do?',
          additionalActions: renderUnconfirmedZoneActions(),
        });
      } else if (useMarqueeZoneStateStore.getState().pendingTargetMarqueeZoneNumber) {
        confirm({
          variant: 'call-to-action',
          catchOnCancel: false,
          title: 'Unconfirmed Marquee Zone',
          description: 'An unconfirmed marquee zone was found. What would you like to do?',
          additionalActions: renderUnconfirmedMarqueeZoneActions(),
        });
      } else if (hasNoColorPoints) {
        showNoColorPointsWarning();
      } else {
        confirm({
          variant: 'info',
          catchOnCancel: false,
          title: 'Unconfirmed Zone',
          description: checkUnconfirmedGraphicZone
            ? 'Please confirm your graphic zones before inspecting the files.'
            : 'Please confirm your match before inspecting the files.',
        });
        if (checkUnconfirmedGraphicZone) {
          PDFManagerFactory.getPDFDocManager()?.selectUnconfirmedGraphicZone();
        } else {
          PDFAnnotationManager.getInstance(DocumentTypes.source)?.deselectAllAnnotations();
          PDFAnnotationManager.getInstance(DocumentTypes.target)?.deselectAllAnnotations();
          dispatch(selectUnconfirmedZones());
        }
      }
    }
  };

  const handleMarqueeToolButtonClicked = (tool: PDFTronTools, noResultId: string) => {
    PDFAnnotationManager.deleteAnnotationById(DocumentTypes.source, noResultId);
    dispatch(inspection.actions.setSelectedTool({ tool: toggleTool(tool, selectedTool, manualSelectedZoneId) }));
    const instance = PDFManagerFactory.getPDFDocManager()?.source;
    if (instance) {
      instance.setTool(PDFTronTools.MARQUEE_ZONE);
      /* eslint-disable */
      // @ts-ignore
      instance.instance.Core.Tools.TextTool.SELECTION_MODE = 'rectangular';
      // Switching tool type so we need to decrease text zone id
      dispatch(inspection.actions.decreaseTextZoneId());
    }
  };

  const handleManualTextSelectionButtonClicked = (noResultId: string) => {
    const zone = zoneSelectedTexts.find((zoneTexts) => {
      return zoneTexts.annotationId === noResultId;
    });
    dispatch(
      inspection.actions.activateManualSelection({
        annotationId: noResultId,
        zoneId: Number(zone?.zoneId),
      }),
    );

    const targetInstance = PDFTronManager.getPDFInstance(DocumentTypes.target);
    targetInstance?.UI.setToolMode(PDFTronTools.ZONE);
    const sourceInstance = PDFTronManager.getPDFInstance(DocumentTypes.source);
    sourceInstance?.UI.setToolMode(PDFTRON_DEFAULT_TOOL);
    PDFManagerFactory.getViewer(DocumentTypes.source)?.removeAnnotationDocumentHighlight();
    PDFManagerFactory.getViewer(DocumentTypes.target)?.addDocumentHighlight();
  };

  const renderConfirmationActions = (noResultId: string) => {
    return (
      <>
        <GVTextButton
          className={styles.action}
          id="manual-select-button"
          text="Manual Selection"
          onClick={() => {
            handleManualTextSelectionButtonClicked(noResultId);
            dispatch(app.actions.setCloseConfirm(true));
          }}
        />
        <Button
          id="marquee-zone-button"
          color="secondary"
          variant="contained"
          onClick={() => {
            handleMarqueeToolButtonClicked(PDFTronTools.MARQUEE_ZONE, noResultId);
            dispatch(app.actions.setCloseConfirm(true));
          }}
        >
          Marquee Selection
        </Button>
      </>
    );
  };

  const handleKeepMarkupsSelection = (keepMarkups: boolean) => {
    if (!pendingShapeRequest) return;
    const { fileId, documentType, type, layerOrSeparationName } = pendingShapeRequest;

    if (keepMarkups) {
      dispatch(
        inspection.actions.setPolygons({
          documentType,
          points: '',
          type: '',
          key: '',
          layerOrSeparationName: '',
        }),
      );
      dispatch(inspection.actions.setPendingShapeRequest(null));
    } else {
      PDFAnnotationManager.deleteAllInputAnnotations();
      const pdfDocMgr = PDFManagerFactory.getPDFDocManager();
      pdfDocMgr?.updateInputAnnotations();
      dispatch(startShapesJob(fileId, documentType, type, layerOrSeparationName));
      dispatch(inspection.actions.setPendingShapeRequest(null));
    }
    dispatch(app.actions.setCloseConfirm(true));
  };

  const handleKeepShapesSelection = (keepShapes: boolean) => {
    if (!pendingCropZone) return;
    const { documentType, pendingCropId } = pendingCropZone;

    if (keepShapes) {
      PDFAnnotationManager.deleteAnnotationById(documentType as DocumentTypes, pendingCropId);
      const pdfDocMgr = PDFManagerFactory.getPDFDocManager();
      pdfDocMgr?.updateInputAnnotations();
      pdfDocMgr?.redrawInputAnnotations();
      dispatch(inspection.actions.setPendingCropZone({ documentType: null, pendingCropId: '' }));
    } else {
      dispatch(
        inspection.actions.setPolygons({
          documentType: documentType as DocumentTypes,
          points: '',
          key: '',
          type: '',
          layerOrSeparationName: '',
        }),
      );
      dispatch(inspection.actions.setPendingCropZone({ documentType: null, pendingCropId: '' }));
    }
    dispatch(app.actions.setCloseConfirm(true));
  };

  const renderDecisionButtons = (item: 'shape' | 'markups', handleSelection: (keep: boolean) => void) => {
    const KEEP_ITEM = true;
    return (
      <>
        <GVTextButton
          className={styles.action}
          id={`keep-${item}-button`}
          text={item === 'shape' ? `Keep Shape` : `Keep markups`}
          onClick={() => handleSelection(KEEP_ITEM)}
        />
        <Button
          id={`override-${item}-button`}
          onClick={() => handleSelection(!KEEP_ITEM)}
          variant="contained"
          color="secondary"
        >
          {item === 'shape' ? `Deactivate Shape` : `Delete markups`}
        </Button>
      </>
    );
  };

  // kick users out of prep tab if the inspection is shared
  useEffect(() => {
    // Guard against acting on a previous inspection's shared state while a
    // newly-selected linked inspection is still loading (route id !== loaded id).
    if (
      shouldRedirectSharedInspectionToResults({
        routeInspectionId,
        loadedInspectionId: inspectionId,
        isShared,
        isOnResultPage: isResultUrl(location),
        isImported: settings.isImported,
      })
    ) {
      navigate(`/inspection/${inspectionId}/result`, { replace: true });
    }
  }, [isShared, settings.isImported, inspectionId, routeInspectionId, location.pathname, navigate]);

  useEffect(() => {
    if (unconfirmMarqueeZoneError) {
      confirm({
        variant: 'info',
        catchOnCancel: true,
        title: 'Unconfirmed Zone',
        description: 'Please confirm your match before creating or unmatching a new one.',
      })
        .then(() => {
          dispatch(inspection.actions.setUnconfirmedMarqueeZoneError(false));
        })
        .catch(() => {
          dispatch(inspection.actions.setUnconfirmedMarqueeZoneError(false));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, unconfirmMarqueeZoneError]);

  useEffect(() => {
    if (unconfirmTextZoneError) {
      confirm({
        variant: 'info',
        catchOnCancel: true,
        title: 'Unconfirmed Zone',
        description: 'Please confirm your match before creating or unmatching a new one.',
      })
        .then(() => {
          dispatch(inspection.actions.setUnconfirmTextZoneError(false));
          dispatch(selectUnconfirmedZones());
        })
        .catch(() => {
          dispatch(inspection.actions.setUnconfirmTextZoneError(false));
          dispatch(selectUnconfirmedZones());
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, unconfirmTextZoneError]);

  useEffect(() => {
    if (unconfirmGraphicZoneError) {
      confirm({
        variant: 'info',
        catchOnCancel: true,
        title: 'Unconfirmed Zone',
        description: 'Please confirm the graphic zone before creating a new one.',
      })
        .then(() => {
          dispatch(inspection.actions.setUnconfirmGraphicZoneError(false));
          PDFManagerFactory.getPDFDocManager()?.selectUnconfirmedGraphicZone();
        })
        .catch(() => {
          dispatch(inspection.actions.setUnconfirmGraphicZoneError(false));
          PDFManagerFactory.getPDFDocManager()?.selectUnconfirmedGraphicZone();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, unconfirmGraphicZoneError]);

  useEffect(() => {
    if (changeToolError !== '') {
      confirm({
        variant: 'info',
        catchOnCancel: false,
        title: checkUnconfirmedGraphicZone || hasUnconfirmedZones ? 'Unconfirmed Zone' : 'One at a time ✋',
        description: changeToolError,
      });
      if (checkUnconfirmedGraphicZone) {
        PDFManagerFactory.getPDFDocManager()?.selectUnconfirmedGraphicZone();
      }
      dispatch(inspection.actions.setChangeToolError(''));
    }
  }, [dispatch, changeToolError]);

  useEffect(() => {
    if (pendingCropZone.pendingCropId) {
      confirm({
        title: 'Deactivate shape?',
        variant: 'call-to-action',
        catchOnCancel: false,
        description:
          'Drawing a crop zone will deactivate the active shape from the Layers menu. Would you like to continue?',
        additionalActions: renderDecisionButtons('shape', handleKeepShapesSelection),
      })
        .catch(() => {
          dispatch(inspection.actions.setPendingCropZone({ documentType: null, pendingCropId: '' }));
        })
        .finally(() => {
          dispatch(inspection.actions.setPendingCropZone({ documentType: null, pendingCropId: '' }));
        });
    }
  }, [pendingCropZone.pendingCropId]);

  useEffect(() => {
    if (pendingShapeRequest) {
      confirm({
        title: 'Delete markups?',
        variant: 'call-to-action',
        catchOnCancel: false,
        description:
          'Activating this shape will remove previous markups from the document. Would you like to continue?',
        additionalActions: renderDecisionButtons('markups', handleKeepMarkupsSelection),
      })
        .catch(() => {
          dispatch(inspection.actions.setPendingShapeRequest(null));
        })
        .finally(() => {
          dispatch(inspection.actions.setPendingShapeRequest(null));
        });
    }
  }, [pendingShapeRequest?.fileId]);

  useEffect(() => {
    if (noResultAnnotationId) {
      confirm({
        title: 'No Match Found',
        variant: 'call-to-action',
        catchOnCancel: false,
        description:
          'We were unable to find a match for your selection. You can either manually select a match or toggle and use the marquee zone tool.',
        additionalActions: renderConfirmationActions(noResultAnnotationId),
      }).then(() => {
        const sourceAnnoManager = PDFAnnotationManager.getInstance(DocumentTypes.source);
        sourceAnnoManager?.selectAnnotation(sourceAnnoManager.getAnnotationById(noResultAnnotationId));
        dispatch(inspection.actions.setNoResultAnnotationId(''));
        // reset this state so auto selection won't beak
        dispatch(inspection.actions.selectingZone(false));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, noResultAnnotationId]);

  // reset zoneId if there's no zone in the list
  useEffect(() => {
    if (!zoneSelectedTexts.length) {
      dispatch(inspection.actions.setTextZoneId(1));
    }
  }, [dispatch, zoneSelectedTexts.length]);

  // Clean up color picker tool on unmount if Color Check is active
  useEffect(() => {
    return () => {
      if (colorCheckRef.current) {
        selectTool(PDFTRON_DEFAULT_TOOL);
        const pdfDocManager = PDFManagerFactory.getPDFDocManager();
        pdfDocManager?.cleanDocumentHighlight();
      }
    };
  }, [dispatch]);

  // Shared inspections hide Prep. VerifyAPI imports stay shared and still show it.
  if (prepLocked) {
    return (
      <div className={styles.root}>
        <Spinner.Root />
      </div>
    );
  }
  const inspectButtonIsDisabled =
    !textProcessCompleted ||
    !annotationLoaded ||
    !documentsLoaded ||
    startingInspection ||
    (!settings.barcode &&
      !settings.brailleLanguage &&
      !settings.spelling &&
      !settings.fullPageGraphics &&
      !settings.text &&
      !settings.fontSizeCheckValue &&
      !settings.colorCheck &&
      !hasConfirmedGraphicZones) ||
    !validFontSizeCheck ||
    (settings.spelling && (!settings.dictionaryName || settings.dictionaryName?.length === 0));

  const toolTipText = () => {
    if (
      isSingleFile &&
      textProcessCompleted &&
      !settings.barcode &&
      !settings.brailleLanguage &&
      !settings.dictionaryName &&
      !settings.fontSizeCheckValue &&
      !settings.colorCheck
    )
      return 'Enable Spellcheck, Barcode or Braille, Font Size Check, or Color Check to run a single document inspection';
    if (settings.spelling && (!settings.dictionaryName || settings.dictionaryName?.length === 0))
      return 'Select a dictionary or disable spellcheck';
    if (inspectButtonIsDisabled) return 'Enable at least one inspection type to continue';
    return '';
  };

  return (
    <div className={styles.root}>
      <PrepTabAlerts />
      <div className={styles.content}>
        <div className={styles.title}>
          <canvas id="imgProcessing" style={{ display: 'none' }} />
          <Typography variant="h3" color="textPrimary">
            {isSingleFile ? 'Prepare your file for inspection' : 'Prepare your files for inspection'}
          </Typography>
        </div>
        <AnnotationsTools />
        <div className={`${styles.overflow} ${classes.scrollBar}`}>
          <GVInspectionPanel />
        </div>
      </div>
      <div className={styles.bar}>
        <GVTooltip title={toolTipText()} side="bottom">
          <PointerBoundary>
            <GVActionButton
              label={
                startingInspection ? (
                  <Spinner.Root />
                ) : (
                  <>
                    <GVIcon className={styles.icon} />
                    Inspect
                  </>
                )
              }
              onClick={handleStartInspection}
              disabled={inspectButtonIsDisabled}
            />
          </PointerBoundary>
        </GVTooltip>
      </div>
    </div>
  );
};

export default PrepTab;
